'use client'

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import CompanyDropdownData from '@/components/navbar/CompanyDropdownData'
import ProductsDropdownData from '@/components/navbar/ProductsDropdownData'
import PartnersDropdownData from '@/components/navbar/PartnersDropdownData'
import ResourcesDropdownData from '@/components/navbar/ResourcesDropdownData'
import SolutionsDropdownData from '@/components/navbar/SolutionsDropdownData'
import { useEscapeKey } from '@/hooks/useEscapeKey'
import { cn } from '@/utils/twMerge'

import type { MegaKey } from '@/components/navbar/megaKeys'

type NavbarMegaTrayProps = {
  activeKey: MegaKey | null
  data: Record<string, unknown>
  trayId: string
  onClose: () => void
  onPointerEnter: () => void
  onPointerLeave: () => void
}

export type NavbarMegaTrayHandle = {
  focusFirst: () => void
}

const PANEL_CLASS: Record<MegaKey, string> = {
  products: 'products-dropdown',
  solutions: 'solutions-dropdown',
  partners: 'partners-dropdown',
  resources: 'resources-dropdown',
  company: 'company-dropdown',
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Keep fade + height on the same clock */
const MOTION_MS = 320

function MegaPanel({ megaKey, data }: { megaKey: MegaKey; data: Record<string, unknown> }) {
  switch (megaKey) {
    case 'products':
      return <ProductsDropdownData data={data as never} />
    case 'solutions':
      return <SolutionsDropdownData data={data as never} />
    case 'partners':
      return <PartnersDropdownData data={data as never} />
    case 'resources':
      return <ResourcesDropdownData data={data as never} />
    case 'company':
      return <CompanyDropdownData data={data as never} />
    default:
      return null
  }
}

function paintThen(callback: () => void) {
  let raf2 = 0
  const raf1 = requestAnimationFrame(() => {
    raf2 = requestAnimationFrame(callback)
  })
  return () => {
    cancelAnimationFrame(raf1)
    cancelAnimationFrame(raf2)
  }
}

function getTrayFocusable(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') return false
    // Skip links inside aria-hidden / outgoing layers
    if (element.closest('[aria-hidden="true"]')) return false
    return true
  })
}

function focusOpenTrigger() {
  const trigger = document.querySelector<HTMLElement>('.navbar-nav .dropdown.is-open > .navbar-nav-link')
  trigger?.focus()
}

function focusNextAfterOpenTrigger(openLi: Element | null) {
  if (!openLi) return

  let sibling = openLi.nextElementSibling
  while (sibling) {
    const target = sibling.querySelector<HTMLElement>('a[href], button:not([disabled])')
    if (target) {
      target.focus()
      return
    }
    sibling = sibling.nextElementSibling
  }

  const action = document.querySelector<HTMLElement>(
    '.navbar-action a[href], .navbar-action button:not([disabled])',
  )
  action?.focus()
}

const NavbarMegaTray = forwardRef<NavbarMegaTrayHandle, NavbarMegaTrayProps>(function NavbarMegaTray(
  { activeKey, data, trayId, onClose, onPointerEnter, onPointerLeave },
  ref,
) {
  const trayRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const prevKeyRef = useRef<MegaKey | null>(null)
  const [height, setHeight] = useState(0)
  const [renderKey, setRenderKey] = useState<MegaKey | null>(null)
  const [outgoingKey, setOutgoingKey] = useState<MegaKey | null>(null)
  const [incomingIn, setIncomingIn] = useState(false)
  const [outgoingOut, setOutgoingOut] = useState(false)
  const isOpen = activeKey != null

  useEscapeKey(onClose, isOpen)

  useImperativeHandle(
    ref,
    () => ({
      focusFirst: () => {
        const tray = trayRef.current
        if (!tray) return
        const focusFirst = () => getTrayFocusable(tray)[0]?.focus()
        // Panel may still be mounting / measuring
        focusFirst()
        requestAnimationFrame(focusFirst)
      },
    }),
    [],
  )

  useEffect(() => {
    if (!activeKey) {
      setIncomingIn(false)
      setOutgoingOut(true)
      // Reset synchronously so re-entering the same key before MOTION_MS
      // takes the first-open path instead of hitting `prev === activeKey`.
      prevKeyRef.current = null
      const t = window.setTimeout(() => {
        setRenderKey(null)
        setOutgoingKey(null)
        setOutgoingOut(false)
        setHeight(0)
      }, MOTION_MS)
      return () => window.clearTimeout(t)
    }

    const prev = prevKeyRef.current

    // First open (also covers close interrupted by re-hovering the same trigger)
    if (!prev) {
      setOutgoingKey(null)
      setRenderKey(activeKey)
      prevKeyRef.current = activeKey
      setIncomingIn(false)
      setOutgoingOut(false)
      return paintThen(() => setIncomingIn(true))
    }

    if (prev === activeKey) {
      // Defensive: settle open if a close animation was interrupted without clearing prev
      setOutgoingKey(null)
      setOutgoingOut(false)
      setIncomingIn(true)
      return
    }
    // Switch: start height + crossfade together (no wait between fade out / fade in)
    setOutgoingKey(prev)
    setOutgoingOut(false)
    setRenderKey(activeKey)
    setIncomingIn(false)
    prevKeyRef.current = activeKey

    const cancelPaint = paintThen(() => {
      setOutgoingOut(true)
      setIncomingIn(true)
    })

    const clearOutgoing = window.setTimeout(() => {
      setOutgoingKey(null)
      setOutgoingOut(false)
    }, MOTION_MS)

    return () => {
      cancelPaint()
      window.clearTimeout(clearOutgoing)
    }
  }, [activeKey])

  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el || !isOpen) return

    const measure = () => {
      if (measureRef.current) {
        setHeight(measureRef.current.scrollHeight)
      }
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [renderKey, isOpen])

  // Disclosure keyboard: Tab cycles trigger → tray links → next nav control (not a full trap)
  useEffect(() => {
    if (!isOpen) return
    const tray = trayRef.current
    if (!tray) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusable = getTrayFocusable(tray)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const activeElement = document.activeElement

      if (event.shiftKey && activeElement === first) {
        event.preventDefault()
        focusOpenTrigger()
        return
      }

      if (!event.shiftKey && activeElement === last) {
        event.preventDefault()
        // Capture next control before close removes `.is-open`
        const openLi = document.querySelector('.navbar-nav > .dropdown.is-open')
        onClose()
        requestAnimationFrame(() => focusNextAfterOpenTrigger(openLi))
      }
    }

    tray.addEventListener('keydown', onKeyDown)
    return () => tray.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, activeKey])

  return (
    <div
      ref={trayRef}
      id={trayId}
      className={cn('mega-tray', isOpen && 'is-open')}
      data-open={isOpen ? 'true' : 'false'}
      aria-hidden={!isOpen}
      inert={!isOpen ? true : undefined}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}>
      <div className="mega-tray-inner" style={{ height: isOpen ? height : 0 }}>
        <div className="mega-tray-layers">
          {outgoingKey && (
            <div
              className={cn(
                'mega-tray-panel mega-tray-panel--outgoing',
                PANEL_CLASS[outgoingKey],
                outgoingOut && 'is-out',
              )}
              aria-hidden="true">
              <MegaPanel megaKey={outgoingKey} data={data} />
            </div>
          )}
          {renderKey && (
            <div
              key={renderKey}
              ref={measureRef}
              className={cn(
                'mega-tray-panel mega-tray-panel--incoming',
                PANEL_CLASS[renderKey],
                incomingIn && 'is-in',
              )}>
              <MegaPanel megaKey={renderKey} data={data} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default NavbarMegaTray
